import React, { useState, useEffect, useRef, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { useListRecipes } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Download,
  FileSpreadsheet,
  CheckCircle2,
  LayoutList,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

function getBaseUrl(): string {
  return import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
}

function triggerDownload(filter: string, ids?: number[], categories?: string[]) {
  const base = getBaseUrl();
  const params = new URLSearchParams({ filter });
  if (ids && ids.length > 0) params.set("ids", ids.join(","));
  if (categories && categories.length > 0) params.set("categories", categories.join(","));
  window.open(`${base}/api/export/excel?${params.toString()}`, "_blank");
}

// ─── Booklet types ─────────────────────────────────────────────────────────

type BookletStatus = "queued" | "processing" | "completed" | "failed";

interface BookletRow {
  bookletId: number;
  status: BookletStatus;
  title: string;
  recipeCount: number;
  renderedCount: number;
  fileSize: number | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  downloadUrl?: string;
}

type BookletFilterKind = "all" | "validated" | "category" | "cuisine";

function formatBytes(n: number | null): string {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ExportPage() {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [catOpen, setCatOpen] = useState(false);

  // Booklet state
  const [bkFilter, setBkFilter] = useState<BookletFilterKind>("all");
  const [bkCategory, setBkCategory] = useState<string>("");
  const [bkCuisine, setBkCuisine] = useState<string>("");
  const [booklets, setBooklets] = useState<BookletRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const pollingRef = useRef<number | null>(null);

  const { data, isLoading } = useListRecipes();
  const recipes = data?.recipes ?? [];

  const allCategories = [
    ...new Set(recipes.map((r) => r.category).filter(Boolean) as string[]),
  ].sort();

  // Sort cuisines by recipe count (desc) so the most useful ones surface first
  // in the dropdown. The data is messy (33 variants for ~270 recipes) but
  // the spec calls for a cuisine filter, so we expose it.
  const cuisineCounts = new Map<string, number>();
  for (const r of recipes) {
    const c = (r.cuisine ?? "").trim();
    if (!c) continue;
    cuisineCounts.set(c, (cuisineCounts.get(c) ?? 0) + 1);
  }
  const allCuisines = [...cuisineCounts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );

  const validatedCount = recipes.filter(
    (r) => r.validationStatus === "validated"
  ).length;

  const categoryFiltered = selectedCategories.length > 0
    ? recipes.filter((r) => r.category && selectedCategories.includes(r.category))
    : recipes;

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  // ─── Booklet polling ─────────────────────────────────────────────────────
  // Refresh the list every 3s while any booklet is queued/processing,
  // and just once on mount otherwise.

  const refreshBooklets = useCallback(async () => {
    try {
      const res = await fetch(`${getBaseUrl()}/api/booklets`);
      if (!res.ok) return;
      const json = (await res.json()) as { booklets: BookletRow[] };
      setBooklets(json.booklets ?? []);
    } catch {
      /* swallow — will retry on next tick */
    }
  }, []);

  useEffect(() => {
    void refreshBooklets();
  }, [refreshBooklets]);

  useEffect(() => {
    const anyInFlight = booklets.some(
      (b) => b.status === "queued" || b.status === "processing",
    );
    if (anyInFlight && pollingRef.current === null) {
      pollingRef.current = window.setInterval(() => {
        void refreshBooklets();
      }, 3000);
    } else if (!anyInFlight && pollingRef.current !== null) {
      window.clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current !== null) {
        window.clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [booklets, refreshBooklets]);

  const handleCreateBooklet = async () => {
    setCreateError(null);
    setCreating(true);
    try {
      const body: Record<string, string> = { filter: bkFilter };
      if (bkFilter === "category") {
        if (!bkCategory) {
          setCreateError("Pick a category first");
          setCreating(false);
          return;
        }
        body.value = bkCategory;
      }
      if (bkFilter === "cuisine") {
        if (!bkCuisine) {
          setCreateError("Pick a cuisine first");
          setCreating(false);
          return;
        }
        body.value = bkCuisine;
      }
      const res = await fetch(`${getBaseUrl()}/api/booklets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCreateError(
          (json as { error?: string }).error ?? `Failed (${res.status})`,
        );
        setCreating(false);
        return;
      }
      await refreshBooklets();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setCreating(false);
    }
  };

  const downloadBooklet = (b: BookletRow) => {
    window.open(`${getBaseUrl()}/api/booklets/${b.bookletId}/download`, "_blank");
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h2 className="text-3xl font-serif font-bold text-foreground">Export</h2>
          <p className="text-muted-foreground mt-1">
            Download your recipe collection as an Excel workbook or a printable PDF booklet
          </p>
        </div>

        {/* ── PDF Booklet ─────────────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="w-5 h-5 text-amber-700" />
              Print-Ready PDF Booklet
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Generate a single bound PDF with a cover, table of contents,
              and one recipe card per page. Great for printing or sharing
              the whole collection.
            </p>

            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {(["all", "validated", "category", "cuisine"] as const).map((k) => (
                  <button
                    key={k}
                    onClick={() => setBkFilter(k)}
                    className={cn(
                      "text-sm px-3 py-1.5 rounded-md border transition-colors",
                      bkFilter === k
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-border hover:bg-muted",
                    )}
                  >
                    {k === "all" && `All recipes (${recipes.length})`}
                    {k === "validated" && `Validated only (${validatedCount})`}
                    {k === "category" && "By category"}
                    {k === "cuisine" && "By cuisine"}
                  </button>
                ))}
              </div>

              {bkFilter === "category" && (
                <select
                  value={bkCategory}
                  onChange={(e) => setBkCategory(e.target.value)}
                  className="w-full border border-border bg-background rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Pick a category…</option>
                  {allCategories.map((c) => {
                    const n = recipes.filter((r) => r.category === c).length;
                    return (
                      <option key={c} value={c}>
                        {c} ({n})
                      </option>
                    );
                  })}
                </select>
              )}

              {bkFilter === "cuisine" && (
                <select
                  value={bkCuisine}
                  onChange={(e) => setBkCuisine(e.target.value)}
                  className="w-full border border-border bg-background rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Pick a cuisine…</option>
                  {allCuisines.map(([name, n]) => (
                    <option key={name} value={name}>
                      {name} ({n})
                    </option>
                  ))}
                </select>
              )}

              {createError && (
                <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{createError}</span>
                </div>
              )}

              <Button
                onClick={handleCreateBooklet}
                disabled={
                  creating ||
                  isLoading ||
                  (bkFilter === "category" && !bkCategory) ||
                  (bkFilter === "cuisine" && !bkCuisine)
                }
                className="gap-2 w-full sm:w-auto"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <BookOpen className="w-4 h-4" />
                )}
                Generate PDF Booklet
              </Button>
            </div>

            {booklets.length > 0 && (
              <div className="pt-2 border-t border-border space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Recent booklets
                </p>
                {booklets.slice(0, 5).map((b) => (
                  <div
                    key={b.bookletId}
                    className="flex items-center justify-between gap-3 text-sm bg-muted/40 px-3 py-2 rounded-md"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">
                        {b.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {b.status === "queued" && (
                          <span className="inline-flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Waiting in queue…
                          </span>
                        )}
                        {b.status === "processing" && (
                          <span className="inline-flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Rendering {b.renderedCount} of {b.recipeCount}…
                          </span>
                        )}
                        {b.status === "completed" && (
                          <>
                            {b.recipeCount} recipes ·{" "}
                            {formatBytes(b.fileSize)}
                          </>
                        )}
                        {b.status === "failed" && (
                          <span className="text-destructive">
                            Failed: {b.errorMessage ?? "Unknown error"}
                          </span>
                        )}
                      </p>
                    </div>
                    {b.status === "completed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadBooklet(b)}
                        className="gap-1.5 shrink-0"
                      >
                        <Download className="w-3.5 h-3.5" />
                        PDF
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Excel workbook info ─────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Excel Workbook Contents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <span className="font-semibold text-foreground w-28 shrink-0">Recipes sheet</span>
              <span className="text-muted-foreground">
                One row per recipe — title, author, category, cuisine, times, directions, notes, status
              </span>
            </div>
            <div className="flex items-start gap-3">
              <span className="font-semibold text-foreground w-28 shrink-0">Ingredients sheet</span>
              <span className="text-muted-foreground">
                One row per ingredient — linked to recipe, with amount, unit, item, prep notes
              </span>
            </div>
            <div className="flex items-start gap-3">
              <span className="font-semibold text-foreground w-28 shrink-0">Summary sheet</span>
              <span className="text-muted-foreground">
                Counts by category, validation status, and source type
              </span>
            </div>
            <p className="text-xs text-muted-foreground pt-1">
              File saved as <code className="font-mono bg-muted px-1 rounded">Recipe_Binder_Export_YYYY-MM-DD.xlsx</code>
            </p>
          </CardContent>
        </Card>

        {/* Excel export options */}
        <div className="space-y-3">
          {/* All recipes */}
          <Card className="hover:shadow-sm transition-shadow">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 p-2.5 rounded-xl text-primary mt-0.5">
                    <LayoutList className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Export All Recipes</p>
                    <p className="text-sm text-muted-foreground">
                      {isLoading ? "Loading…" : `${recipes.length} recipe${recipes.length !== 1 ? "s" : ""} total`}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => triggerDownload("all")}
                  disabled={isLoading || recipes.length === 0}
                  className="gap-2 shrink-0"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Validated only */}
          <Card className="hover:shadow-sm transition-shadow">
            <CardContent className="pt-5 pb-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 p-2.5 rounded-xl text-green-700 mt-0.5">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Export Validated Only</p>
                    <p className="text-sm text-muted-foreground">
                      {isLoading ? "Loading…" : `${validatedCount} validated recipe${validatedCount !== 1 ? "s" : ""}`}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => triggerDownload("validated")}
                  disabled={isLoading || validatedCount === 0}
                  className="gap-2 shrink-0"
                >
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* By category */}
          {allCategories.length > 0 && (
            <Card className="hover:shadow-sm transition-shadow">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="bg-blue-100 p-2.5 rounded-xl text-blue-700 mt-0.5">
                      <FileSpreadsheet className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-foreground">Export by Category</p>
                      <p className="text-sm text-muted-foreground mb-3">
                        {selectedCategories.length > 0
                          ? `${categoryFiltered.length} recipe${categoryFiltered.length !== 1 ? "s" : ""} in ${selectedCategories.length} category${selectedCategories.length !== 1 ? "ies" : ""}`
                          : "Select one or more categories below"}
                      </p>
                      <button
                        onClick={() => setCatOpen((v) => !v)}
                        className="flex items-center gap-1 text-sm text-primary font-medium"
                      >
                        {catOpen ? (
                          <ChevronUp className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                        {catOpen ? "Hide categories" : "Choose categories"}
                      </button>
                      {catOpen && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {allCategories.map((cat) => {
                            const count = recipes.filter((r) => r.category === cat).length;
                            return (
                              <button
                                key={cat}
                                onClick={() => toggleCategory(cat)}
                                className={cn(
                                  "text-xs px-3 py-1 rounded-full border transition-colors",
                                  selectedCategories.includes(cat)
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background border-border hover:bg-muted"
                                )}
                              >
                                {cat} ({count})
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() =>
                      triggerDownload("all", undefined, selectedCategories)
                    }
                    disabled={isLoading || selectedCategories.length === 0 || categoryFiltered.length === 0}
                    className="gap-2 shrink-0 mt-1"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
